// utils.js — Pure normalisation utilities, workload-type predicates, and format helpers.
// Extracted from script.js (lines 2002–2179).
// No dependencies on script.js — imports only from workload-defs.js.

import { unstructuredDataWorkloadId, repoMigrationWorkloadId,
         veeamBackupForMicrosoft365WorkloadId, kastenK10WorkloadId,
         legacyWorkloadValueAliases, inDevelopmentWorkloadValues,
         hypervisorLabels, workloadUnitMap } from './workload-defs.js';

export function escapeHtml(value) {
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

export function isUnstructuredDataWorkload(value) {
    return value === unstructuredDataWorkloadId;
}

export function isRepoMigrationWorkload(value) {
    return value === repoMigrationWorkloadId;
}

export function isProtectedTbWorkload(value) {
    return isUnstructuredDataWorkload(value) || isRepoMigrationWorkload(value);
}

/** Returns true for Kasten K10 / Kubernetes workloads.
 *  The predicate name is a historical misnomer — Kasten's count unit is clusters,
 *  not namespaces. The label is now driven by workloadUnitMap; this predicate is
 *  retained for routing/classification logic in recalc.js. */
export function isNamespaceScopedWorkload(value) {
    return value === kastenK10WorkloadId;
}

export function isUserScopedWorkload(value) {
    return value === veeamBackupForMicrosoft365WorkloadId;
}

/** Returns true for workloads managed by VB365 (not VBR) — specifically Microsoft 365. */
export function isVb365ManagedWorkload(value) {
    return value === veeamBackupForMicrosoft365WorkloadId;
}

/**
 * Cloud-provider configs used by the Backup Server Mapping canvas.
 * Each entry drives tray/lane UI, drag-group isolation, and colour theming.
 * Entra ID is intentionally absent — it is VBR-native.
 */
export const CLOUD_PROVIDER_CONFIGS = {
    aws: {
        id:          'aws',
        label:       'Veeam Backup for AWS',
        shortLabel:  'VBfAWS',
        serverLabel: 'VBfAWS Appliance',
        sortGroup:   's2-aws',
        workloadSet: new Set([
            'aws-ec2','aws-rds','aws-dynamodb',
            'aws-redshift','aws-redshift-serverless',
            'aws-efs','aws-fsx','aws-vpc',
        ]),
    },
    azure: {
        id:          'azure',
        label:       'Veeam Backup for Azure',
        shortLabel:  'VBfAzure',
        serverLabel: 'VBfAzure Appliance',
        sortGroup:   's2-azure',
        workloadSet: new Set([
            'azure-vm','azure-sql','azure-cosmosdb',
            'azure-files','azure-vnet',
        ]),
    },
    gcp: {
        id:          'gcp',
        label:       'Veeam Backup for GCP',
        shortLabel:  'VBfGCP',
        serverLabel: 'VBfGCP Appliance',
        sortGroup:   's2-gcp',
        workloadSet: new Set([
            'gcp-vm','gcp-sql','gcp-spanner',
        ]),
    },
};

/** Returns 'aws'|'azure'|'gcp' if the workload is managed by a VBfCloud appliance, else null. */
export function getVbfCloudProvider(value) {
    for (const [provider, cfg] of Object.entries(CLOUD_PROVIDER_CONFIGS)) {
        if (cfg.workloadSet.has(value)) return provider;
    }
    return null;
}

/** Returns true for workloads managed by VBfAWS / VBfAzure / VBfGCP (not VBR). */
export function isVbfCloudManagedWorkload(value) {
    return getVbfCloudProvider(value) !== null;
}

export function normalizeWorkloadValue(value) {
    if (typeof value !== 'string') {
        return '';
    }

    return legacyWorkloadValueAliases[value] || value;
}

// Units that must not be lowercased in summaries — storage units and abbreviations.
const PRESERVE_CASE_UNITS = new Set(['TB', 'GB', 'VMs', 'VM', 'VPCs', 'VPC', 'VNets', 'VNet']);

/** Returns the quantity label for a workload type (e.g. 'VMs', 'TB', 'Shares').
 *  Checks workloadUnitMap (built from workloadOptionGroups) first so that adding
 *  a new workload type with a unit field automatically propagates here. */
export function getWorkloadQuantityLabel(value) {
    const normalizedValue = normalizeWorkloadValue(value);

    // Primary lookup: workloadUnitMap is the single source of truth.
    const mapped = workloadUnitMap[normalizedValue];
    if (mapped) {
        // Title-case the first character for display (e.g. 'shares' → 'Shares'),
        // but preserve all-caps/abbreviation units (VMs, VPCs, VNets, TB) as-is.
        return PRESERVE_CASE_UNITS.has(mapped)
            ? mapped
            : mapped.charAt(0).toUpperCase() + mapped.slice(1);
    }

    // Fallbacks for workloads not in workloadOptionGroups (e.g. vdc-vault-migration-data).
    if (isProtectedTbWorkload(normalizedValue)) return 'TB';

    return 'Workloads';
}

export function getWorkloadQuantityPlaceholder(value) {
    const label = getWorkloadQuantityLabel(value);
    if (label === 'TB' || label === 'GB') return `Enter ${label}`;
    // Preserve case for abbreviations (VMs, VPCs, VNets); lowercase everything else.
    const displayLabel = PRESERVE_CASE_UNITS.has(label) ? label : label.toLowerCase();
    return `Enter # of ${displayLabel}`;
}

export function getWorkloadQuantitySummary(value, count) {
    const normalizedCount = Math.max(0, parseInt(count, 10) || 0);
    const quantityLabel = getWorkloadQuantityLabel(value);
    // Singularise for count === 1 by stripping trailing 's' (e.g. Shares → Share).
    // Don't singularise abbreviation units (VMs → VM is fine, but do it via the set check).
    const summaryLabel = normalizedCount === 1 && quantityLabel.endsWith('s')
        ? quantityLabel.slice(0, -1)
        : quantityLabel;

    // Preserve case for abbreviations and storage units.
    return `${normalizedCount} ${PRESERVE_CASE_UNITS.has(summaryLabel) ? summaryLabel : summaryLabel.toLowerCase()}`;
}

export function normalizeProtectedTb(value) {
    const parsedValue = typeof value === 'number' ? value : parseFloat(value);
    if (!Number.isFinite(parsedValue)) {
        return 0;
    }

    return Math.max(0, Math.round(parsedValue * 100) / 100);
}

export function calculateUnstructuredDataInstances(protectedTb) {
    const normalizedProtectedTb = normalizeProtectedTb(protectedTb);
    return normalizedProtectedTb === 0 ? 0 : Math.ceil(normalizedProtectedTb / 0.5);
}

export function formatProtectedTbValue(value) {
    return String(Number(normalizeProtectedTb(value).toFixed(2)));
}

export function formatProtectedTbWholeNumber(value) {
    return String(Math.round(normalizeProtectedTb(value)));
}

export function normalizeEnvironmentCount(value) {
    return Math.max(0, parseInt(value, 10) || 0);
}

/**
 * Normalises a field that was historically stored as a toggle ('yes'/'no') but is now
 * stored as a count. Treats 'yes'/true → 1, 'no'/false → 0, numerics pass through.
 */
export function normalizeToggleMigratedCount(value) {
    if (value === 'yes' || value === true) return 1;
    if (value === 'no' || value === false || value === null || value === undefined) return 0;
    return normalizeEnvironmentCount(value);
}

